/**
 * Typed client for the public API (spec section 14).
 *
 * The server answers in exactly one envelope:
 *
 *   success:    { ok: true,  data: { ... } }
 *   validation: { ok: false, error: { code: 'VALIDATION_ERROR', fields: {...} } }
 *   other:      { ok: false, error: { code: 'INTERNAL_ERROR' | 'RATE_LIMITED' | ... } }
 *
 * This module is the only place in the client that knows that. Everything
 * above it sees a discriminated result: `ok`, or a failure with a `kind` a form
 * can render.
 *
 * A transport failure — offline, DNS, a proxy eating the request — is a
 * `network` failure, not an error to throw. Someone standing in a barn aisle
 * on one bar of signal must get "that did not go through, try again", not a
 * blank page.
 */

import type { OwnerSignupInput } from '../../shared/schemas/owner-signup';
import type { ProviderSignupInput } from '../../shared/schemas/provider-signup';
import type { ReferralInput } from '../../shared/schemas/referral';

/** Field-keyed validation messages, exactly as spec section 14 puts them on the wire. */
export type ApiFieldErrors = Record<string, string>;

export type ApiFailure =
  /** The server rejected specific fields. Merge these into the form. */
  | { ok: false; kind: 'validation'; fields: ApiFieldErrors }
  /** Too many submissions from this address. Retryable, later. */
  | { ok: false; kind: 'rate_limited' }
  /** The request never completed. Retryable now. */
  | { ok: false; kind: 'network' }
  /** The server failed, or answered something this client cannot read. */
  | { ok: false; kind: 'server' };

export type ApiResult<T> = { ok: true; data: T } | ApiFailure;

/** What a successful signup returns: one opaque reference, and nothing else. */
export interface SignupResponse {
  signupRef: string;
}

/**
 * A referral returns an empty object: no id, and nothing about whether the
 * signup token attached it to a lead (spec sections 14, 17).
 */
export type ReferralResponse = Record<string, never>;

export interface CategoryOption {
  slug: string;
  displayName: string;
  isAnchor: boolean;
}

/**
 * Same origin in both environments: Express serves the client in production,
 * and Vite proxies `/api` to Express in development (vite.config.ts).
 */
const API_BASE = '/api';

/** A submission that takes this long is not going to arrive. */
const REQUEST_TIMEOUT_MS = 15_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Reads the error half of the envelope without trusting any of its shape. */
function readFailure(status: number, body: unknown): ApiFailure {
  const error = isRecord(body) && isRecord(body.error) ? body.error : undefined;
  const code = typeof error?.code === 'string' ? error.code : undefined;

  if (code === 'VALIDATION_ERROR') {
    const fields: ApiFieldErrors = {};
    if (isRecord(error?.fields)) {
      for (const [key, message] of Object.entries(error.fields)) {
        if (typeof message === 'string') fields[key] = message;
      }
    }
    return { ok: false, kind: 'validation', fields };
  }

  if (code === 'RATE_LIMITED' || status === 429) return { ok: false, kind: 'rate_limited' };
  return { ok: false, kind: 'server' };
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * One request, one result. `readData` narrows the `data` half; anything it
 * cannot recognise is a `server` failure rather than a cast, because a client
 * that trusts an unexpected response shape fails somewhere less obvious.
 */
async function request<T>(
  path: string,
  init: RequestInit,
  readData: (data: unknown) => T | undefined,
): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      headers: { Accept: 'application/json', ...init.headers },
    });
  } catch {
    return { ok: false, kind: 'network' };
  }

  const body = await readJson(response);

  if (!response.ok || !isRecord(body) || body.ok !== true) {
    return readFailure(response.status, body);
  }

  const data = readData(body.data);
  if (data === undefined) return { ok: false, kind: 'server' };
  return { ok: true, data };
}

function readSignupResponse(data: unknown): SignupResponse | undefined {
  if (!isRecord(data) || typeof data.signupRef !== 'string' || data.signupRef === '') {
    return undefined;
  }
  return { signupRef: data.signupRef };
}

function readReferralResponse(data: unknown): ReferralResponse | undefined {
  // Success is the envelope, not the payload. Anything object-shaped is a
  // success; the endpoint has nothing to tell the browser.
  return isRecord(data) ? ({} as ReferralResponse) : undefined;
}

function readCategories(data: unknown): CategoryOption[] | undefined {
  if (!isRecord(data) || !Array.isArray(data.categories)) return undefined;
  const categories: CategoryOption[] = [];
  for (const entry of data.categories) {
    if (!isRecord(entry)) continue;
    if (typeof entry.slug !== 'string' || typeof entry.displayName !== 'string') continue;
    categories.push({
      slug: entry.slug,
      displayName: entry.displayName,
      isAnchor: entry.isAnchor === true,
    });
  }
  return categories;
}

function postJson(payload: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  };
}

/**
 * `POST /api/signup/owner`. The payload is the *parsed* shared schema output,
 * so the browser sends normalized values — the server re-validates all of it
 * regardless (spec section 14).
 */
export function postOwnerSignup(payload: OwnerSignupInput): Promise<ApiResult<SignupResponse>> {
  return request('/signup/owner', postJson(payload), readSignupResponse);
}

/** `POST /api/signup/provider`. */
export function postProviderSignup(
  payload: ProviderSignupInput,
): Promise<ApiResult<SignupResponse>> {
  return request('/signup/provider', postJson(payload), readSignupResponse);
}

/**
 * `POST /api/referrals`. The signup token travels in the body, never in the URL
 * (spec section 19), and the server is what resolves it.
 *
 * Nothing is sent to the person named — this writes a row in an outreach queue
 * a human works by hand (spec section 17).
 */
export function postReferral(payload: ReferralInput): Promise<ApiResult<ReferralResponse>> {
  return request('/referrals', postJson(payload), readReferralResponse);
}

/**
 * `GET /api/categories`. The provider form's category controls are driven by
 * this rather than by a copy compiled into the bundle, so a category promoted
 * in admin appears without a deploy.
 */
export function fetchCategories(): Promise<ApiResult<CategoryOption[]>> {
  return request('/categories', { method: 'GET' }, readCategories);
}

/**
 * `GET /api/unsubscribe?token=`. Called once, on load, by the `/unsubscribe`
 * page (spec section 17).
 *
 * The token is the only thing that identifies the recipient, and it is an
 * encrypted blob — no address appears in this URL, in the response, or in the
 * page (src/server/services/unsubscribeToken.ts). A bad token comes back as a
 * `validation` failure, which the page renders as one plain error state.
 */
export function requestUnsubscribe(token: string): Promise<ApiResult<Record<string, never>>> {
  return request(`/unsubscribe?token=${encodeURIComponent(token)}`, { method: 'GET' }, (data) =>
    isRecord(data) ? ({} as Record<string, never>) : undefined,
  );
}
